import {Injectable, signal} from '@angular/core';
import {ApiService} from './api/api.service';
import {RealtimeService} from './realtime/realtime.service';
import {Leave, LeaveType} from '../classes/leave';
import {Utils} from '../utils.class';
import {WebsocketService} from './websocket/websocket.service';
import {RealtimeType} from './realtime/realtime-type.enum';
import {formatDate} from '@angular/common';
import {Settings} from '../settings.class';
import {Registration} from '../classes/registration';
import {tap} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class LeaveService extends RealtimeService<Leave> {
    static readonly unapprovedLeavesCount = signal<number>(0);

    constructor(public apiService: ApiService, protected websocketService: WebsocketService) {
        super(websocketService, RealtimeType.leaves);
    }

    itemInDaterange(leave: Leave, fromDate: Date, toDate: Date) {
        return Utils.getTimeOrNull(leave.begindate) <= Utils.getTimeOrNull(toDate)
            && Utils.getTimeOrNull(leave.enddate) >= Utils.getTimeOrNull(fromDate);
    }

    getLeaves(history = false, startDate?: Date, endDate?: Date) {
        const fromDate = startDate?.toUTCString() ?? null;
        const toDate = endDate?.toUTCString() ?? null;
        return this.apiService.getCallData$<Leave[]>('leave', {
            ...(this.apiService.buildParams({
                history, fromDate, toDate
            }))
        });
    }

    getLeave(id: number) {
        return this.apiService.getCallData$<Leave>(`leave/${id}`);
    }

    getLeaveRealisations(id: number) {
        return this.apiService.getCallData$<Registration[]>(`leave/realisations/${id}`);
    }

    saveLeave(leave: Leave) {
        return this.apiService.postCall$<Leave>('leave', leave).pipe(tap(() => {
            this.getUnapprovedLeaves().subscribe();
        }));
    }

    approveLeave(id: number) {
        return this.apiService.postCall$<Leave>(`leave/${id}`).pipe(tap(() => {
            this.getUnapprovedLeaves().subscribe();
        }));
    }

    deleteLeave(id) {
        return this.apiService.deleteCall$<Leave>(`leave/${id}`).pipe(tap(() => {
            this.getUnapprovedLeaves().subscribe();
        }));
    }

    generateLeaveDates(leave: Leave): { beginDate, endDate }[] {
        const begintime = new Date();
        if (!leave.begintime) {
            Utils.setTime(begintime, Settings.DEFAULT_START, 0, 0);
        } else {
            const time = leave.begintime.split(':');
            Utils.setTime(begintime, +time[0], +time[1], 0);
        }
        const endtime = new Date();
        if (!leave.endtime) {
            Utils.setTime(endtime, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);
        } else {
            const time = leave.endtime.split(':');
            Utils.setTime(endtime, +time[0], +time[1], 0);
        }

        const dates = [];
        const loopDate = new Date(leave.begindate);
        const lastEndDate = new Date(leave.enddate);
        const firstWeek = +formatDate(loopDate, 'w', 'nl');
        const startTime = new Date(leave.begindate);
        Utils.setTime(startTime, begintime.getHours(), begintime.getMinutes());
        const endTime = new Date(leave.enddate);
        Utils.setTime(endTime, endtime.getHours(), endtime.getMinutes());

        const loopEndDate = new Date(leave.enddate);
        while (loopDate.getTime() < loopEndDate.getTime()) {

            let generateThisDay = !(leave.interval_week > 0);
            if (leave.interval_week > 0) {
                const week = +formatDate(loopDate, 'w', 'nl');
                const thisWeek = ((week - firstWeek) % leave.interval_week) === 0;
                const weekDayName = formatDate(loopDate, 'EEEE', 'en').toLowerCase();
                const thisDay = leave[`repeat_${weekDayName}`];
                generateThisDay = thisWeek && thisDay;
            }

            if (generateThisDay) {
                const beginDate = new Date(loopDate);
                if (beginDate.getTime() === Utils.getTimeOrNull(leave.begindate) && beginDate.getTime() > startTime.getTime()) {
                    Utils.setTime(beginDate, (new Date(leave.begindate)).getHours(), (new Date(leave.begindate)).getMinutes(), 0);
                } else {
                    Utils.setTime(beginDate, startTime.getHours(), startTime.getMinutes(), 0);
                }

                const endDate = new Date(loopDate);
                Utils.setTime(endDate, (new Date(leave.enddate)).getHours(), (new Date(leave.enddate)).getMinutes(), 0);

                if (endDate.getTime() !== lastEndDate.getTime() || lastEndDate.getTime() > endTime.getTime()) {
                    Utils.setTime(endDate, endTime.getHours(), endTime.getMinutes(), 0);
                }

                const realisation = {beginDate, endDate};
                dates.push(realisation);
            }
            loopDate.setDate(loopDate.getDate() + 1);
        }
        return dates;
    }

    getLeaveTypes() {
        return this.apiService.getCallData$<LeaveType[]>('leave/type');
    }

    getUnapprovedLeaves(startDate?: Date, endDate?: Date) {
        return this.apiService.getCallData$<number>('leave/unapproved',).pipe(tap(
            (leaves) => {
                LeaveService.unapprovedLeavesCount.set(leaves);
            },));
    }
}
